import React, { useEffect, useMemo, useState } from 'react';
import { User } from '../../types';
import './Avatar.css';

interface AvatarProps {
  user?: Partial<User> | null;
  name?: string;
  avatarUrl?: string;
  avatarColor?: string;
  size?: number;
  className?: string;
  title?: string;
  initialLimit?: number;
}

function buildInitials(name: string, limit: number): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';

  return trimmed
    .split(' ')
    .filter(Boolean)
    .map(part => part[0]?.toUpperCase() || '')
    .join('')
    .slice(0, limit) || '?';
}

export const Avatar: React.FC<AvatarProps> = ({
  user,
  name,
  avatarUrl,
  avatarColor,
  size = 40,
  className = '',
  title,
  initialLimit = 2,
}) => {
  const resolvedName = name || user?.name || '';
  const resolvedAvatarUrl = avatarUrl ?? user?.avatarUrl;
  const resolvedAvatarColor = avatarColor || user?.avatarColor || 'var(--rae-blue)';
  const [imageLoaded, setImageLoaded] = useState(false);

  const initials = useMemo(() => buildInitials(resolvedName, initialLimit), [resolvedName, initialLimit]);

  useEffect(() => {
    if (!resolvedAvatarUrl) {
      setImageLoaded(false);
      return;
    }

    let cancelled = false;
    const image = new Image();

    setImageLoaded(false);

    image.onload = () => {
      if (!cancelled) setImageLoaded(true);
    };

    image.onerror = () => {
      if (!cancelled) setImageLoaded(false);
    };

    image.src = resolvedAvatarUrl;

    return () => {
      cancelled = true;
    };
  }, [resolvedAvatarUrl]);

  return (
    <span
      className={`app-avatar ${className}`.trim()}
      style={{ width: size, height: size, background: resolvedAvatarColor }}
      title={title || resolvedName}
    >
      {resolvedAvatarUrl && imageLoaded ? (
        <img
          className="app-avatar-image"
          src={resolvedAvatarUrl}
          alt={resolvedName || 'User avatar'}
        />
      ) : (
        <span className="app-avatar-fallback">{initials}</span>
      )}
    </span>
  );
};
